// Recovery copy of the device credentials (token / uuid / id) in localStorage.
//
// All IndexedDB object stores for an origin share ONE LevelDB backing file. An
// unclean power-off on the Pi can corrupt it, after which Chromium discards the
// entire IndexedDB (token + content + logs together) and the device drops back
// to the pairing screen. localStorage uses a *separate* backing store, so a copy
// kept here survives that specific failure: on boot we reseed IndexedDB from it
// and the device stays paired, only needing to re-download content.
//
// This does NOT survive a whole-origin eviction or broad filesystem corruption
// (both clear localStorage too) — it only guards the common IndexedDB-corruption
// case. The real cure for power-loss corruption is a clean shutdown.

const KEYS = {
  token: 'vz_device_token',
  uuid: 'vz_device_uuid',
  id: 'vz_device_id',
} as const;

export interface CredentialBackup {
  token: string;
  uuid: string;
  deviceId: number;
}

export function saveCredentialBackup(b: Partial<CredentialBackup>): void {
  try {
    if (b.token !== undefined) localStorage.setItem(KEYS.token, b.token);
    if (b.uuid !== undefined) localStorage.setItem(KEYS.uuid, b.uuid);
    if (b.deviceId !== undefined) localStorage.setItem(KEYS.id, String(b.deviceId));
  } catch {
    // localStorage unavailable / disabled; backup is best-effort.
  }
}

export function readCredentialBackup(): CredentialBackup | null {
  try {
    const token = localStorage.getItem(KEYS.token) ?? '';
    if (!token) return null;
    return {
      token,
      uuid: localStorage.getItem(KEYS.uuid) ?? '',
      deviceId: Number(localStorage.getItem(KEYS.id) ?? '0') || 0,
    };
  } catch {
    return null;
  }
}

export function clearCredentialBackup(): void {
  try {
    localStorage.removeItem(KEYS.token);
    localStorage.removeItem(KEYS.uuid);
    localStorage.removeItem(KEYS.id);
  } catch {
    // ignore
  }
}
